import { IContactCompany } from 'models/contact_companies'
import { CompanyFormShape, CompanyModal } from './CompanyModal'
import { useMemo } from 'react'
import apiService from 'services/api'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useMutation } from '@tanstack/react-query'

export function EditCompanyModal({
  show,
  setShow,
  company,
  skipDealCreation = false,
}: {
  show: boolean
  skipDealCreation?: boolean
  setShow: (show: boolean) => void
  company: IContactCompany
}) {
  const api = apiService()
  const queryClient = useQueryClient()

  const apiAction = async (values: CompanyFormShape) => {
    delete values.taglist
    delete values.distributors
    const payload = {
      ...values,
      name: values.name?.trim() || '',
      country: values.country || 'US',
      state: values.state || '',
      city: values.city || '',
      domain: values.domain?.toLowerCase() || '',
      address: values.address || '',
      zipcode: values.zipcode || '',
    }
    const response = await api
      .updateCompany(company.id, payload)
      .then((response) => {
        toast.success('Company updated successfully')
        return response
      })
    return response
  }

  const { mutateAsync } = useMutation({
    mutationFn: apiAction,
    onMutate: async (values: CompanyFormShape) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['company', company.id],
      })

      // Snapshot the previous value
      const previousCompany = queryClient.getQueryData(['company', company.id])

      // Optimistically update the cache
      queryClient.setQueryData(
        ['company', company.id],
        (old: IContactCompany) => {
          if (!old) return old
          return {
            ...old,
            name: values.name?.trim() || old.name,
            domain: values.domain?.toLowerCase() || old.domain,
            country: values.country || old.country,
            state: values.state || old.state,
            city: values.city || old.city,
            address: values.address || old.address,
            zipcode: values.zipcode || old.zipcode,
            phone_number: values.phone_number || old.phone_number,
            extension: values.extension || old.extension,
          }
        }
      )

      // Return context for rollback
      return { previousCompany }
    },
    onError: (err, values, context) => {
      // Rollback on error
      if (context?.previousCompany) {
        queryClient.setQueryData(
          ['company', company.id],
          context.previousCompany
        )
      }
      toast.error('Failed to update company')
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['company', company.id],
      })
    },
  })

  const optimisticApiAction = async (values: CompanyFormShape) => {
    return await mutateAsync(values)
  }

  const defaultValues: CompanyFormShape = useMemo(() => {
    return {
      ...company,
      category: company.category_id,
      sub_category: company.sub_category_id,
      distributors: company.distributors?.map((d) => d.id),
      taglist: company.taglist?.map((tag) => tag.id),
      unique_domain: true,
    }
  }, [company])

  return (
    <CompanyModal
      show={show}
      setShow={setShow}
      hideKnownDistributorsAndTags
      defaultValues={defaultValues}
      skipDealCreation={skipDealCreation}
      apiAction={optimisticApiAction}
      title="Edit Company"
      description="Add or modify information about this company."
      acceptButtonText="Apply Changes"
    />
  )
}
